import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ComposedModal,
  ModalHeader,
  ModalBody,
  Button,
  Search,
  Checkbox,
  RadioButtonGroup,
  RadioButton,
  Slider,
} from '@carbon/react';
import { useAtom, useAtomValue } from 'jotai';
import classNames from 'classnames';
import { TcUnitFilters, FilterCategory, TcUnitsFacets } from '../types';
import type {
  DevelopmentAreaPicklistFacet,
  DevelopmentAreaRangeFacet,
} from 'types/api-responses';
import { TC_UNIT_FILTER_CATEGORIES } from './utils';

import {
  tcUnitFilterModalOpenAtom,
  tcUnitFilterModalCategoryAtom,
  tcUnitFiltersAtom,
  tcPendingUnitFiltersAtom,
  tcUnitsFacetsAtom,
} from './atoms';
import { tcEffectiveWorkspaceIdAtom } from 'components/type-curve-workspace/atoms';
import { format0 } from 'utils/number';
import styles from './tc-well-filter-modal.module.scss';

interface FacetOption {
  id: string;
  name: string;
}

function isPicklistFacet(
  value: unknown
): value is DevelopmentAreaPicklistFacet {
  return (
    typeof value === 'object' &&
    value !== null &&
    Array.isArray((value as { buckets?: unknown }).buckets)
  );
}

function getUnitsFacetPicklistOptions(
  category: FilterCategory,
  facets: TcUnitsFacets | null
): FacetOption[] {
  if (!facets || !category.facetKey) return [];
  const facet = facets[category.facetKey as keyof TcUnitsFacets];
  if (!isPicklistFacet(facet)) return [];
  return facet.buckets
    .map((b) => ({
      id: String(b.id),
      name: b.name ?? String(b.id),
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

function getUnitsFacetNumericRange(
  category: FilterCategory,
  facets: TcUnitsFacets | null
): { min: number; max: number } | null {
  if (!facets || !category.facetKey) return null;
  const facet = facets[category.facetKey as keyof TcUnitsFacets];
  if (isPicklistFacet(facet)) return null;
  const range = facet as DevelopmentAreaRangeFacet | undefined;
  if (
    !range ||
    range.min === null ||
    range.min === undefined ||
    range.max === null ||
    range.max === undefined
  )
    return null;
  return {
    min: Math.floor(Number(range.min)),
    max: Math.ceil(Number(range.max)),
  };
}

const CategoryList = ({
  activeCategory,
  onSelect,
}: {
  activeCategory: string;
  onSelect: (id: string) => void;
}) => {
  const [search, setSearch] = useState('');
  const effectiveWorkspaceId = useAtomValue(tcEffectiveWorkspaceIdAtom);
  const categories = TC_UNIT_FILTER_CATEGORIES.filter(
    (cat) => effectiveWorkspaceId !== null || cat.id !== 'verificationStatus'
  ).filter((cat) => cat.label.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className={styles.categoriesInner}>
      <div className={styles.categoriesSearch}>
        <Search
          labelText="Search filters"
          placeholder="Search Filter Names"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      <ul className={styles.categoryList}>
        {categories.map((cat) => (
          <li key={cat.id} className={styles.categoryItem}>
            <button
              type="button"
              onClick={() => onSelect(cat.id)}
              className={classNames(styles.categoryButton, {
                [styles.categoryButtonActive]: activeCategory === cat.id,
              })}
            >
              {cat.label}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

const PicklistFormContent = ({
  category,
  facets,
  selected,
  onChange,
}: {
  category: FilterCategory;
  facets: TcUnitsFacets | null;
  selected: string[];
  onChange: (values: string[]) => void;
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const options = useMemo(
    () => getUnitsFacetPicklistOptions(category, facets),
    [category, facets]
  );
  const filteredOptions = useMemo(() => {
    return searchTerm
      ? options.filter((o) =>
          o.name.toLowerCase().includes(searchTerm.toLowerCase())
        )
      : options;
  }, [options, searchTerm]);

  const filteredIds = useMemo(
    () => filteredOptions.map((o) => o.id),
    [filteredOptions]
  );

  const allSelected =
    filteredIds.length > 0 && filteredIds.every((id) => selected.includes(id));

  return (
    <>
      <Search
        labelText={`Search ${category.label}`}
        placeholder={`Search ${category.label.toLowerCase()}...`}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        size="sm"
      />
      <div className={styles.selectRow}>
        <button
          type="button"
          className={styles.selectAllLink}
          onClick={() => {
            if (allSelected) {
              onChange(selected.filter((v) => !filteredIds.includes(v)));
            } else {
              onChange([...new Set([...selected, ...filteredIds])]);
            }
          }}
        >
          {allSelected ? 'Deselect All' : 'Select All'}
        </button>
        <span className={styles.resultCount}>
          {filteredOptions.length} results
        </span>
      </div>
      <div className={styles.fieldStack}>
        {filteredOptions.map((option) => (
          <Checkbox
            key={option.id}
            id={`tc-unit-filter-${category.id}-${option.id}`}
            labelText={option.name}
            checked={selected.includes(option.id)}
            onChange={(_evt, { checked }) => {
              onChange(
                checked
                  ? [...selected, option.id]
                  : selected.filter((v) => v !== option.id)
              );
            }}
          />
        ))}
        {filteredOptions.length === 0 && (
          <p className={styles.noResults}>No options available</p>
        )}
      </div>
    </>
  );
};

// Shown until the facet response loads its true/false buckets. Names match the
// facet buckets (value.to_s) so labels don't change once facets arrive.
const BOOLEAN_RADIO_FALLBACK: FacetOption[] = [
  { id: 'false', name: 'false' },
  { id: 'true', name: 'true' },
];

const BooleanRadioFormContent = ({
  category,
  facets,
  selected,
  onChange,
}: {
  category: FilterCategory;
  facets: TcUnitsFacets | null;
  selected: string[];
  onChange: (values: string[]) => void;
}) => {
  const options = useMemo(() => {
    const facetOptions = getUnitsFacetPicklistOptions(category, facets);
    return facetOptions.length > 0 ? facetOptions : BOOLEAN_RADIO_FALLBACK;
  }, [category, facets]);
  // Single-select: anything other than an explicit 'true' is the false default.
  const current = selected[0] === 'true' ? 'true' : 'false';

  return (
    <div className={styles.fieldStack}>
      <RadioButtonGroup
        legendText=""
        name={`tc-unit-filter-${category.id}`}
        valueSelected={current}
        orientation="vertical"
        onChange={(value: string | number | undefined) =>
          onChange([String(value)])
        }
      >
        {options.map((option) => (
          <RadioButton
            key={option.id}
            id={`tc-unit-filter-${category.id}-${option.id}`}
            labelText={option.name}
            value={option.id}
          />
        ))}
      </RadioButtonGroup>
    </div>
  );
};

function computeSliderStep(min: number, max: number): number {
  const range = max - min;
  if (range <= 0) return 1;
  if (range <= 10) return 0.1;
  if (range <= 100) return 1;
  if (range <= 1000) return 10;
  if (range <= 10000) return 100;
  return 1000;
}

const NumericRangeFormContent = ({
  category,
  facets,
  minValue,
  maxValue,
  onChange,
}: {
  category: FilterCategory;
  facets: TcUnitsFacets | null;
  minValue?: number;
  maxValue?: number;
  onChange: (min: number | undefined, max: number | undefined) => void;
}) => {
  const range = useMemo(
    () => getUnitsFacetNumericRange(category, facets),
    [category, facets]
  );

  const rangeMin = range?.min ?? 0;
  const rangeMax = range?.max ?? 100;
  const step = computeSliderStep(rangeMin, rangeMax);

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  return (
    <div className={styles.sliderGroup}>
      <Slider
        id={`${category.id}-range`}
        labelText={category.label}
        hideLabel
        ariaLabelInput="Min"
        unstable_ariaLabelInputUpper="Max"
        min={rangeMin}
        max={rangeMax}
        value={minValue ?? rangeMin}
        unstable_valueUpper={maxValue ?? rangeMax}
        step={step}
        stepMultiplier={Math.max(1, Math.round(10 / step))}
        formatLabel={(v: number) => format0(v)}
        onChange={({
          value,
          valueUpper,
        }: {
          value: number;
          valueUpper?: number;
        }) => {
          onChangeRef.current(value, valueUpper);
        }}
        onRelease={({
          value,
          valueUpper,
        }: {
          value: number;
          valueUpper?: number;
        }) => {
          onChangeRef.current(value, valueUpper);
        }}
      />
    </div>
  );
};

const FilterForm = ({
  activeCategory,
  pending,
  onChange,
  facets,
}: {
  activeCategory: string;
  pending: TcUnitFilters;
  onChange: (filters: TcUnitFilters) => void;
  facets: TcUnitsFacets | null;
}) => {
  const category = TC_UNIT_FILTER_CATEGORIES.find(
    (c) => c.id === activeCategory
  );
  if (!category) return null;

  const hasCategoryFilter = (() => {
    if (category.type === 'picklist') {
      const vals = pending[category.param];
      return Array.isArray(vals) && vals.length > 0;
    }
    const hasMin =
      category.minParam && pending[category.minParam] !== undefined;
    const hasMax =
      category.maxParam && pending[category.maxParam] !== undefined;
    return !!(hasMin || hasMax);
  })();

  const selectedPills: string[] = (() => {
    if (category.type === 'picklist') {
      return (pending[category.param] as string[] | undefined) ?? [];
    }
    if (category.type === 'numeric-range') {
      const min = pending[category.minParam!] as number | undefined;
      const max = pending[category.maxParam!] as number | undefined;
      if (min === undefined && max === undefined) return [];
      if (min !== undefined && max !== undefined)
        return [`${format0(min)} – ${format0(max)}`];
      if (min !== undefined) return [`Min: ${format0(min)}`];
      return [`Max: ${format0(max)}`];
    }
    return [];
  })();

  const handleClear = () => {
    const updated = { ...pending };
    if (category.type === 'picklist') {
      // Single-select booleans clear back to their false default, never "any".
      updated[category.param] = category.singleSelect ? ['false'] : [];
    } else {
      if (category.minParam) delete updated[category.minParam];
      if (category.maxParam) delete updated[category.maxParam];
    }
    onChange(updated);
  };

  let content: React.ReactNode = null;

  if (category.type === 'picklist') {
    const selected = (pending[category.param] as string[] | undefined) ?? [];
    content = category.singleSelect ? (
      <BooleanRadioFormContent
        key={category.id}
        category={category}
        facets={facets}
        selected={selected}
        onChange={(values) =>
          onChange({ ...pending, [category.param]: values })
        }
      />
    ) : (
      <PicklistFormContent
        key={category.id}
        category={category}
        facets={facets}
        selected={selected}
        onChange={(values) =>
          onChange({ ...pending, [category.param]: values })
        }
      />
    );
  } else if (category.type === 'numeric-range') {
    content = (
      <NumericRangeFormContent
        category={category}
        facets={facets}
        minValue={pending[category.minParam!] as number | undefined}
        maxValue={pending[category.maxParam!] as number | undefined}
        onChange={(min, max) =>
          onChange({
            ...pending,
            [category.minParam!]: min,
            [category.maxParam!]: max,
          })
        }
      />
    );
  }

  return (
    <div className={styles.formWrapper}>
      <h4 className={styles.formTitle}>{category.label}</h4>
      <div className={styles.selectedFilters}>
        <span className={styles.selectedFiltersLabel}>Selected Filters</span>
        {selectedPills.length === 0 ? (
          <span className={styles.selectedFiltersEmpty}>
            No filters selected
          </span>
        ) : (
          <div className={styles.selectedFiltersPills}>
            {selectedPills.map((pill) => (
              <span key={pill} className={styles.selectedFiltersPill}>
                {pill}
              </span>
            ))}
          </div>
        )}
      </div>
      <div className={styles.formContent}>{content}</div>
      <div className={styles.formFooter}>
        <button
          type="button"
          className={styles.clearLink}
          onClick={handleClear}
          disabled={!hasCategoryFilter}
        >
          Clear
        </button>
      </div>
    </div>
  );
};

export const TcUnitFilterModal = () => {
  const [isOpen, setIsOpen] = useAtom(tcUnitFilterModalOpenAtom);
  const targetCategory = useAtomValue(tcUnitFilterModalCategoryAtom);
  const [appliedFilters, setAppliedFilters] = useAtom(tcUnitFiltersAtom);
  const [pending, setPending] = useAtom(tcPendingUnitFiltersAtom);
  const facets = useAtomValue(tcUnitsFacetsAtom);
  const [activeCategory, setActiveCategory] = useState<string>(
    TC_UNIT_FILTER_CATEGORIES[0].id
  );

  useEffect(() => {
    if (isOpen) {
      setPending({ ...appliedFilters });
      // Open on the category the caller asked for (an applied filter's edit
      // icon); the plain "Add Filter" path clears it and lands on the first.
      setActiveCategory(targetCategory ?? TC_UNIT_FILTER_CATEGORIES[0].id);
    }
  }, [isOpen, appliedFilters, setPending, targetCategory]);

  const handleApply = () => {
    setAppliedFilters({ ...pending });
    setIsOpen(false);
  };

  const handleCancel = () => {
    setPending({ ...appliedFilters });
    setIsOpen(false);
  };

  const handleReset = () => {
    setPending({});
  };

  return (
    <ComposedModal
      open={isOpen}
      onClose={handleCancel}
      size="lg"
      className={styles.modal}
    >
      <ModalHeader title="Units Filters" />
      <ModalBody>
        <div className={styles.content}>
          <div className={styles.categories}>
            <CategoryList
              activeCategory={activeCategory}
              onSelect={setActiveCategory}
            />
          </div>
          <div className={styles.form}>
            <FilterForm
              activeCategory={activeCategory}
              pending={pending}
              onChange={setPending}
              facets={facets}
            />
          </div>
        </div>
      </ModalBody>
      <div className={styles.customFooter}>
        <Button
          size="lg"
          kind="ghost"
          className={styles.footerButton}
          onClick={handleCancel}
        >
          Cancel
        </Button>
        <Button
          size="lg"
          kind="secondary"
          className={styles.footerButton}
          onClick={handleReset}
        >
          Reset
        </Button>
        <Button
          size="lg"
          kind="primary"
          className={styles.footerButton}
          onClick={handleApply}
        >
          Apply Filters
        </Button>
      </div>
    </ComposedModal>
  );
};
